




















































import { orderBy, keyBy } from 'lodash-es'
import { Component, Vue, Prop } from 'vue-property-decorator'
import { WorkspaceUsers } from '@/views/WorkspaceSettings.vue'
import { Pill, Icon } from '@alpha/ink'

import * as db from '@shared/types/database'
import api from '@/api'
import { format as dateFormat } from 'date-fns'
import fromUnixTime from 'date-fns/fromUnixTime'
import store from '@/store'

enum TestTableColumnName {
  TITLE,
  STATUS,
  AUDIENCE,
  CREATOR,
  DATE_CREATED,
  DATE_COMPLETED
}

const SortDescription: Record<string, [string, string]> = {
  ALPHANUMERIC: ['A', 'Z'],
  DATE: ['Oldest', 'Newest'],
  TEST_STATUS: ['Draft', 'Completed']
}

interface TestTableColumn {
  name: TestTableColumnName
  className: string
  title: string
  sortDescription: [string, string]
  sortDescendingByDefault?: boolean
}

const testTableColumns: TestTableColumn[] = [
  {
    name: TestTableColumnName.TITLE,
    className: 'title',
    title: 'Test Title',
    sortDescription: SortDescription.ALPHANUMERIC
  },
  {
    name: TestTableColumnName.STATUS,
    className: 'status',
    title: 'Status',
    sortDescription: SortDescription.TEST_STATUS,
    sortDescendingByDefault: true
  },
  {
    name: TestTableColumnName.AUDIENCE,
    className: 'audience',
    title: 'Audience',
    sortDescription: SortDescription.ALPHANUMERIC
  },
  {
    name: TestTableColumnName.CREATOR,
    className: 'creator',
    title: 'Creator',
    sortDescription: SortDescription.ALPHANUMERIC
  },
  {
    name: TestTableColumnName.DATE_CREATED,
    className: 'date',
    title: 'Date Created',
    sortDescription: SortDescription.DATE,
    sortDescendingByDefault: true
  },
  {
    name: TestTableColumnName.DATE_COMPLETED,
    className: 'date',
    title: 'Date Completed',
    sortDescription: SortDescription.DATE,
    sortDescendingByDefault: true
  }
]

const testTableColumnsByName = keyBy(testTableColumns, c => c.name)

// Borrowed from
// https://stackoverflow.com/questions/11305797/remove-zero-width-space-characters-from-a-javascript-string
function stripZeroWidthCharacters (text: string): string {
  return text.replace(/\u200B/g, '')
}

function getSortableString (text: string): string {
  return text.toLowerCase().trim()
}

@Component({
  components: {
    Pill,
    Icon
  }
})
export default class TestTable extends Vue {
  @Prop({ required: true }) readonly tests!: db.Test[]
  @Prop({ required: true }) readonly workspaceId!: string
  @Prop({ required: false }) readonly projectId?: string

  workspaceUsers: WorkspaceUsers = []
  sortColumn: TestTableColumnName = TestTableColumnName.DATE_CREATED
  sortDescending: boolean = testTableColumnsByName[this.sortColumn].sortDescendingByDefault || false
  testTableColumns = testTableColumns

  async mounted () {
    try {
      const res = await api.getWorkspacePreferences(this.workspaceId)
      this.workspaceUsers = res.workspaceUsers
    } catch (err) {
      console.error(err)
    }
  }

  getSortedValue (test: db.Test, column: TestTableColumnName): number | string {
    switch (column) {
      // Some test titles start with zero-width characters. These can break the sorting order, so we strip
      // out the invalid characters before sorting.
      case TestTableColumnName.TITLE: return stripZeroWidthCharacters(getSortableString(test.payload.title ?? ''))
      case TestTableColumnName.STATUS: return db.TestStatusLifecycleOrder[test.payload.status]
      case TestTableColumnName.AUDIENCE: return getSortableString(this.audience(test.payload))
      case TestTableColumnName.CREATOR: return getSortableString(this.createdBy(test) || '')
      case TestTableColumnName.DATE_CREATED: return test.createdAt
      case TestTableColumnName.DATE_COMPLETED: return test.payload.completedAt || 0
    }
  }

  get sortTestTableColumn () {
    return testTableColumnsByName[this.sortColumn]
  }

  get sortStatus () {
    const [startValue, endValue] = this.sortTestTableColumn.sortDescription
    const values = this.sortDescending ? [endValue, startValue] : [startValue, endValue]
    return `${this.sortTestTableColumn.title} (${values.join(' → ')})`
  }

  get sortedTests () {
    const direction = this.sortDescending ? 'desc' : 'asc'
    return orderBy(this.tests, t => this.getSortedValue(t, this.sortColumn), [direction])
  }

  sort (column: TestTableColumnName) {
    if (this.sortColumn === column) {
      this.sortDescending = !this.sortDescending
    } else {
      this.sortDescending = testTableColumnsByName[column].sortDescendingByDefault || false
      this.sortColumn = column
    }
  }

  audience (testInfo:db.TestPayload): string {
    if (typeof testInfo.segment === 'string') {
      return testInfo.segment
    } else {
      const audience = store.getters.workspace.getAudienceById(testInfo.segment!.id)
      return audience?.name || testInfo.segment?.title || ''
    }
  }

  createdBy (test:db.Test) {
    if (!this.workspaceUsers) return ''
    const creator = this.workspaceUsers.find(user => user.id === test.created_by.id)

    if (!creator) return 'Feedback Loop Admin'

    if (creator) {
      let fullName = creator.payload.firstname
      if (creator.payload.lastname) {
        fullName += ' ' + creator.payload.lastname
      }
      return fullName
    }
  }

  completedDate (test:db.Test) {
    if (test.payload.completedAt) {
      const time:Date = fromUnixTime(test.payload.completedAt)
      return dateFormat(time, 'MM/d/yy')
    } else {
      return 'N/A'
    }
  }
}

